import React, { useMemo, useState } from 'react';
import {
  XMarkIcon,
  PlayIcon,
  TrashIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  Squares2X2Icon,
  BellAlertIcon,
} from '@heroicons/react/24/outline';
import monitorsService from '../../../services/monitorsService';
import {
  useMonitorValues,
  useMonitorAlerts,
  useRunMonitor,
  useDeleteMonitor,
} from '../../../hooks/useMonitors';
import MonitorSparkline from './MonitorSparkline';

// Full-detail modal for a monitor. Triggered by the expand icon at the
// top-right of MonitorCard. Solves the truncation problem: card surface
// is constrained for the grid layout, but the detail modal can spill
// the full name, full SQL query, all evaluation thresholds, and the
// full source/evaluation config without ellipsis.
//
// Sibling to MonitorHistoryModal: that one is a focused chart view with
// time-window controls; this one is a structured info dump. Two clear
// purposes, two modals.
//
// Actions mirror the card (Run Now / Edit / Delete) so the modal works
// as a "expanded card replacement" — user doesn't need to close it and
// reach for the small card buttons.
const MonitorDetailModal = ({
  isOpen,
  onClose,
  monitor,
  connectionId,
  aggregatedValues,
  onEdit,
}) => {
  const [confirmDelete, setConfirmDelete] = useState(false);

  const runMutation = useRunMonitor(connectionId, monitor?.id);
  const deleteMutation = useDeleteMonitor(connectionId);

  // Larger fetch for the per-segment list. Mirrors MonitorCard's
  // expanded fetch — limit=500 gives ~20 history points per segment for
  // a 25-segment monitor. Only fires while the modal is open, so
  // collapsed cards don't pay for it. Same query key as the card's
  // expanded fetch, so they share cache.
  const { data: detailValues = [] } = useMonitorValues(
    connectionId,
    monitor?.id,
    { limit: 500, enabled: isOpen && !!monitor?.id },
  );

  // Open alerts for THIS monitor. Drives the alert-detail section near
  // the top of the modal so users opening from the alerts banner see
  // *what* fired, not just monitor configuration. Pre-2026-05-21 the
  // modal showed config + trends only, leaving users to guess which
  // value crossed which threshold (BUGS.md: ST1).
  const { data: openAlerts = [] } = useMonitorAlerts(
    connectionId,
    monitor?.id,
    { status: 'open', limit: 25, enabled: isOpen && !!monitor?.id },
  );

  // Per-segment breakdown for the modal's segments section. Group by
  // segment_value, sort alphabetically, take the latest reading per
  // segment for the inline value display. Skips NULL-segment rows
  // because those are the un-grouped aggregate (shown separately at
  // the top of the modal).
  const segmentRows = useMemo(() => {
    if (!monitor?.segment_by && !monitor?.source_config?.segment_column) {
      return [];
    }
    const grouped = new Map();
    for (const v of detailValues) {
      const seg = v.segment_value;
      if (seg === null || seg === undefined) continue;
      if (!grouped.has(seg)) grouped.set(seg, []);
      grouped.get(seg).push(v);
    }
    return [...grouped.entries()]
      .sort(([a], [b]) => String(a).localeCompare(String(b)))
      .map(([segment, rows]) => ({
        segment,
        latest: rows[0],
        history: rows,
      }));
  }, [monitor?.segment_by, monitor?.source_config?.segment_column, detailValues]);

  // All four aggregations computed across the LATEST run's per-segment
  // values. Drives the "Aggregations across segments" table in the
  // modal so the user can see what overall could mean here — useful
  // for custom_sql monitors where we can't auto-derive the right
  // aggregate from arbitrary SQL. The table includes the user's
  // chosen default (highlighted) alongside the other three so it's
  // easy to spot if they picked the wrong one.
  const aggregationsTable = useMemo(() => {
    if (!segmentRows || segmentRows.length === 0) return null;
    const latestValues = segmentRows
      .map((r) =>
        typeof r.latest?.value === 'number'
          ? r.latest.value
          : Number(r.latest?.value),
      )
      .filter((v) => Number.isFinite(v));
    if (latestValues.length === 0) return null;
    const sum = latestValues.reduce((a, b) => a + b, 0);
    return {
      sum,
      avg: sum / latestValues.length,
      min: Math.min(...latestValues),
      max: Math.max(...latestValues),
    };
  }, [segmentRows]);

  if (!isOpen || !monitor) return null;

  // Recognize segmentation from monitor.segment_by OR custom_sql's
  // source_config.segment_column. Matches the detection in MonitorCard
  // so older custom_sql monitors created without segment_by still get
  // the segmented treatment (badges, "overall" label).
  const isSegmented =
    !!monitor.segment_by ||
    (monitor.source_type === 'custom_sql' &&
      !!monitor.source_config?.segment_column);
  const evalCfg = monitor.evaluation_config || {};
  const srcCfg = monitor.source_config || {};

  const latest = aggregatedValues?.[0];
  const latestValue = latest?.value;
  const lastCollectedAt = monitor.last_collected_at || latest?.calculated_at;

  // ---- Field renderers ----------------------------------------------
  // Reusable label/value row. `mono` for SQL / column names so they
  // visually separate from prose labels.
  const Field = ({ label, value, mono = false, children }) => (
    <div className="grid grid-cols-3 gap-3 py-1.5 text-sm">
      <div className="text-gray-500">{label}</div>
      <div
        className={`col-span-2 text-gray-900 break-words ${
          mono ? 'font-mono text-xs' : ''
        }`}
      >
        {children ?? value ?? <span className="text-gray-400">—</span>}
      </div>
    </div>
  );

  // Source-section content varies by source_type — render the actual
  // fields the user picked, not a generic blob.
  const renderSource = () => {
    if (monitor.source_type === 'builtin_table_metric') {
      return (
        <>
          <Field label="Type" value="Built-in table metric" />
          <Field label="Table" value={srcCfg.table_name} mono />
          <Field label="Metric" value={srcCfg.metric} mono />
        </>
      );
    }
    if (monitor.source_type === 'builtin_column_metric') {
      return (
        <>
          <Field label="Type" value="Built-in column metric" />
          <Field label="Table" value={srcCfg.table_name} mono />
          <Field label="Column" value={srcCfg.column_name} mono />
          <Field label="Metric" value={srcCfg.metric} mono />
        </>
      );
    }
    if (monitor.source_type === 'custom_sql') {
      return (
        <>
          <Field label="Type" value="Custom SQL" />
          <Field label="Value column" value={srcCfg.value_column} mono />
          {srcCfg.segment_column && (
            <Field label="Segment column" value={srcCfg.segment_column} mono />
          )}
          <div className="pt-2 text-sm">
            <div className="text-gray-500 mb-1">Query</div>
            <pre className="text-xs bg-gray-50 border border-gray-200 rounded p-3 whitespace-pre-wrap break-words font-mono">
              {srcCfg.query || ''}
            </pre>
          </div>
        </>
      );
    }
    return <Field label="Type" value={monitor.source_type} />;
  };

  const renderEvaluation = () => {
    if (monitor.evaluation_mode === 'statistical') {
      return (
        <>
          <Field label="Mode" value="Statistical / ML anomaly detection" />
          <Field label="Method" value={evalCfg.method} mono />
          <Field
            label="Sensitivity"
            value={
              evalCfg.sensitivity != null
                ? Number(evalCfg.sensitivity).toFixed(2)
                : null
            }
          />
          <Field
            label="Baseline window"
            value={
              evalCfg.baseline_window_days != null
                ? `${evalCfg.baseline_window_days} days`
                : null
            }
          />
        </>
      );
    }
    if (monitor.evaluation_mode === 'threshold') {
      return (
        <>
          <Field label="Mode" value="Threshold" />
          <Field label="Direction" value={evalCfg.direction} mono />
          <Field
            label="Warning threshold"
            value={
              evalCfg.warning != null ? String(evalCfg.warning) : null
            }
            mono
          />
          <Field
            label="Critical threshold"
            value={
              evalCfg.critical != null ? String(evalCfg.critical) : null
            }
            mono
          />
        </>
      );
    }
    return <Field label="Mode" value={monitor.evaluation_mode} />;
  };

  // ---- Action handlers ----------------------------------------------
  const handleDelete = async () => {
    if (!confirmDelete) {
      setConfirmDelete(true);
      // Same 4s auto-revert as MonitorCard. The modal will unmount
      // along with the card on a successful delete, so we don't need
      // a ref-cleanup useEffect here — the unmount short-circuits the
      // timer's effects.
      setTimeout(() => setConfirmDelete(false), 4000);
      return;
    }
    await deleteMutation.mutateAsync(monitor.id);
    // Card unmounts on list refetch; modal unmounts with it.
  };

  const handleEdit = () => {
    if (onEdit) onEdit(monitor);
    onClose();
  };

  // ---- Render ------------------------------------------------------
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-start justify-center p-4 pt-12">
        <div
          className="fixed inset-0 bg-black bg-opacity-25"
          onClick={onClose}
        />
        <div className="relative w-full max-w-3xl bg-white rounded-lg shadow-xl">
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200 flex items-start justify-between">
            <div className="min-w-0 flex-1 pr-3">
              <h2 className="text-xl font-semibold text-gray-900 break-words">
                {monitor.display_name || monitor.name}
              </h2>
              {monitor.display_name && monitor.display_name !== monitor.name && (
                <p className="mt-0.5 text-xs text-gray-500 font-mono break-words">
                  {monitor.name}
                </p>
              )}
              {monitor.description && (
                <p className="mt-2 text-sm text-gray-600 whitespace-pre-wrap break-words">
                  {monitor.description}
                </p>
              )}
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 flex-shrink-0"
              aria-label="Close"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          {/* Status badges row */}
          <div className="px-6 py-3 border-b border-gray-200 flex flex-wrap items-center gap-2">
            <span
              className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                monitor.is_active
                  ? 'bg-green-100 text-green-800'
                  : 'bg-gray-100 text-gray-600'
              }`}
            >
              {monitor.is_active ? 'Active' : 'Inactive'}
            </span>
            {isSegmented && (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                <Squares2X2Icon className="h-3 w-3 mr-1" />
                Segmented by {monitor.segment_by
                  || monitor.source_config?.segment_column}
              </span>
            )}
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-800">
              {monitorsService.sourceTypeLabel(monitor.source_type)}
            </span>
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
              {monitor.evaluation_mode === 'statistical'
                ? 'ML / Statistical'
                : 'Threshold'}
            </span>
          </div>

          {/* Latest value + sparkline */}
          <div className="px-6 py-4 grid grid-cols-1 sm:grid-cols-2 gap-4 border-b border-gray-200">
            <div className="min-w-0">
              {/* Truncate guards against numbers that overflow even
                  the wider modal column. title= keeps the full value
                  one hover away. */}
              <div
                className="text-3xl font-semibold text-gray-900 truncate"
                title={monitorsService.formatValue(latestValue)}
              >
                {monitorsService.formatCompact(latestValue)}
              </div>
              <div className="mt-1 text-xs text-gray-500">
                {isSegmented
                  ? (latest?._aggregated === true
                      ? 'avg across segments'
                      : 'overall')
                  : 'latest reading'}
                {lastCollectedAt && (
                  <>
                    <ClockIcon className="inline h-3 w-3 ml-2 mr-1" />
                    {new Date(lastCollectedAt).toLocaleString()}
                  </>
                )}
              </div>
            </div>
            <div className="h-16">
              <MonitorSparkline values={aggregatedValues || []} />
            </div>
          </div>

          {/* Source + Evaluation + Schedule + Segments.
              Scrollable container so super-long custom SQL queries
              don't push the actions off the bottom of the viewport. */}
          <div className="max-h-[50vh] overflow-y-auto">
            {/* Open alerts — shown FIRST so users opening this modal
                from the Monitors-page alerts banner see *what fired*
                without scrolling past config + trends. Each row shows
                the alert type, severity, the value that fired, the
                threshold it breached (when applicable), segment value
                (for segmented monitors), and when it was detected.
                Threshold breaches show `value vs threshold`; statistical
                anomalies show value + score. BUGS.md: ST1
                (2026-05-21). */}
            {openAlerts.length > 0 && (
              <section className="px-6 py-4 border-b border-gray-200 bg-amber-50">
                <h3 className="text-sm font-medium text-amber-900 mb-2 flex items-center">
                  <BellAlertIcon className="h-4 w-4 mr-1.5 text-amber-500" />
                  {openAlerts.length} open alert{openAlerts.length === 1 ? '' : 's'}
                </h3>
                <ul className="divide-y divide-amber-200">
                  {openAlerts.slice(0, 10).map((alert) => (
                    <li
                      key={alert.id}
                      className="py-2 flex flex-wrap items-baseline gap-x-3 text-sm"
                    >
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${monitorsService.getSeverityBadge(
                          alert.severity,
                        )}`}
                      >
                        {alert.severity}
                      </span>
                      <span className="font-medium text-gray-900">
                        {monitorsService.alertTypeLabel(alert.alert_type)}
                      </span>
                      {alert.segment_value && (
                        <span className="font-mono text-xs text-gray-700 bg-white px-1.5 py-0.5 rounded border border-amber-200">
                          {alert.segment_value}
                        </span>
                      )}
                      <span className="text-gray-700">
                        value{' '}
                        <span className="font-mono text-gray-900">
                          {monitorsService.formatCompact(alert.value)}
                        </span>
                        {alert.threshold != null && (
                          <>
                            {' '}vs threshold{' '}
                            <span className="font-mono text-gray-900">
                              {monitorsService.formatCompact(alert.threshold)}
                            </span>
                          </>
                        )}
                        {alert.score != null && alert.threshold == null && (
                          <>
                            {' '}(score{' '}
                            <span className="font-mono text-gray-900">
                              {Number(alert.score).toFixed(2)}
                            </span>
                            )
                          </>
                        )}
                      </span>
                      {alert.detected_at && (
                        <span
                          className="ml-auto text-xs text-gray-500 whitespace-nowrap"
                          title={new Date(alert.detected_at).toISOString()}
                        >
                          <ClockIcon className="inline h-3 w-3 mr-1" />
                          {new Date(alert.detected_at).toLocaleString()}
                        </span>
                      )}
                    </li>
                  ))}
                  {openAlerts.length > 10 && (
                    <li className="py-2 text-xs text-amber-700">
                      Showing 10 of {openAlerts.length} — resolve some to see
                      the rest.
                    </li>
                  )}
                </ul>
              </section>
            )}

            {/* Aggregations table. Only meaningful for segmented
                monitors that fall back to client-side aggregation
                (segment_value=NULL rows absent — i.e., custom_sql or
                a built-in monitor that hasn't been re-run since the
                2026-05-19 aggregate-query feature went live). For
                monitors with engine aggregates the "overall" at the
                top is already mathematically correct and showing
                client-side fallback math next to it would be confusing.
                The aggregation_method on source_config drives which
                row is the "chosen" default; the user sees all four so
                they can revisit the choice. */}
            {isSegmented &&
             aggregationsTable !== null &&
             latest?._aggregated === true && (
              <section className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-sm font-medium text-gray-900 mb-2">
                  Aggregations across segments
                  <span className="ml-2 text-xs font-normal text-gray-500">
                    (latest run, {segmentRows.length} segments)
                  </span>
                </h3>
                {(() => {
                  const chosen =
                    monitor.source_config?.aggregation_method || 'sum';
                  const rows = [
                    {
                      key: 'sum',
                      label: 'Sum',
                      hint: 'total across segments',
                      value: aggregationsTable.sum,
                    },
                    {
                      key: 'avg',
                      label: 'Avg',
                      hint: 'mean of segment values',
                      value: aggregationsTable.avg,
                    },
                    {
                      key: 'min',
                      label: 'Min',
                      hint: 'smallest segment value',
                      value: aggregationsTable.min,
                    },
                    {
                      key: 'max',
                      label: 'Max',
                      hint: 'largest segment value',
                      value: aggregationsTable.max,
                    },
                  ];
                  return (
                    <table className="w-full text-sm">
                      <tbody>
                        {rows.map((r) => {
                          const active = r.key === chosen;
                          return (
                            <tr
                              key={r.key}
                              className={
                                active ? 'bg-indigo-50' : 'border-t border-gray-100'
                              }
                            >
                              <td className="py-1.5 pl-2 w-20">
                                <span className="text-gray-900 font-medium">
                                  {r.label}
                                </span>
                                {active && (
                                  <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-indigo-100 text-indigo-800 uppercase tracking-wide">
                                    Default
                                  </span>
                                )}
                              </td>
                              <td className="py-1.5 text-xs text-gray-500">
                                {r.hint}
                              </td>
                              <td
                                className="py-1.5 pr-2 text-right font-mono text-gray-900"
                                title={monitorsService.formatValue(r.value)}
                              >
                                {monitorsService.formatCompact(r.value)}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  );
                })()}
                <p className="mt-2 text-xs text-gray-500">
                  Change the default in the monitor's Source settings if
                  the highlighted row doesn't match what "overall" should
                  mean for this query.
                </p>
              </section>
            )}

            <section className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-sm font-medium text-gray-900 mb-2">Source</h3>
              {renderSource()}
            </section>

            <section className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-sm font-medium text-gray-900 mb-2">
                Evaluation
              </h3>
              {renderEvaluation()}
            </section>

            <section className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-sm font-medium text-gray-900 mb-2">Schedule</h3>
              <Field
                label="Cadence"
                value={monitorsService.scheduleSummary(monitor)}
              />
              <Field
                label="Minimum data points"
                value={String(monitor.min_data_points)}
              />
            </section>

            {/* Per-segment breakdown. Only rendered for segmented
                monitors. The overall (un-grouped) trend is already
                shown at the top of the modal; this section drills into
                the dimension breakdown so the user can spot which
                segment is moving the needle. */}
            {isSegmented && (
              <section className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-sm font-medium text-gray-900 mb-2">
                  Segments
                  <span className="ml-2 text-xs font-normal text-gray-500">
                    {segmentRows.length}
                    {segmentRows.length === 1 ? ' segment' : ' segments'}
                  </span>
                </h3>
                {segmentRows.length === 0 ? (
                  <p className="text-xs text-gray-500 py-2">
                    No segment data yet. Run the monitor to populate.
                  </p>
                ) : (
                  <ul className="divide-y divide-gray-100">
                    {segmentRows.map(({ segment, latest: segLatest, history }) => (
                      <li
                        key={segment}
                        className="flex items-center py-2 text-sm"
                      >
                        <span
                          className="flex-1 truncate text-gray-900 font-mono text-xs"
                          title={segment}
                        >
                          {segment}
                        </span>
                        <span
                          className="mx-3 font-mono text-gray-700"
                          title={monitorsService.formatValue(segLatest?.value)}
                        >
                          {monitorsService.formatCompact(segLatest?.value)}
                        </span>
                        <div className="w-24 h-8">
                          <MonitorSparkline values={history} />
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            )}
          </div>

          {/* Actions footer */}
          <div className="px-6 py-3 bg-gray-50 flex flex-wrap items-center justify-end gap-2 rounded-b-lg">
            <button
              type="button"
              onClick={() => runMutation.mutate()}
              disabled={runMutation.isPending}
              className="inline-flex items-center px-3 py-1.5 text-sm border border-gray-300 rounded-md bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              {runMutation.isPending ? (
                <ArrowPathIcon className="h-4 w-4 mr-1 animate-spin" />
              ) : (
                <PlayIcon className="h-4 w-4 mr-1" />
              )}
              Run Now
            </button>
            <button
              type="button"
              onClick={handleEdit}
              className="inline-flex items-center px-3 py-1.5 text-sm border border-gray-300 rounded-md bg-white text-gray-700 hover:bg-gray-50"
            >
              Edit
            </button>
            <button
              type="button"
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
              className={`inline-flex items-center px-3 py-1.5 text-sm border rounded-md disabled:opacity-50 ${
                confirmDelete
                  ? 'border-red-300 bg-red-50 text-red-700'
                  : 'border-gray-300 bg-white text-red-600 hover:bg-red-50'
              }`}
              title={confirmDelete ? 'Click again to confirm' : 'Delete monitor'}
            >
              {confirmDelete ? (
                <>
                  <ExclamationTriangleIcon className="h-4 w-4 mr-1" />
                  Confirm delete
                </>
              ) : (
                <>
                  <TrashIcon className="h-4 w-4 mr-1" />
                  Delete
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MonitorDetailModal;
